import React, { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';

// Interface do medicamento analisado
export interface AnalyzedMedication {
  remedy_name: string | null;
  potency_number: string | null;
  potency_type: 'DH' | 'CH' | 'M';
  dosage_type: 'globules' | 'drops';
  quantity: string | null;
  times: string[];
  frequency: 'daily' | 'alternate' | 'weekly' | 'custom';
  custom_days: string[];
  weekly_day_index: number | null;
  usage_days: string | null;
  pause_days: string | null;
  total_period: string;
  notes: string;
}

// Interface dos dados salvos por membro
interface PrescriptionData {
  medications: AnalyzedMedication[];
  selectedIndex: number | null;
  timestamp: number;
  memberId: string;
}

// Interface do mapa de prescrições (múltiplos membros)
interface PrescriptionMap {
  [memberId: string]: PrescriptionData;
}

// Interface do Context
interface PrescriptionContextType {
  medications: AnalyzedMedication[];
  selectedIndex: number | null;
  setMedications: (medications: AnalyzedMedication[], memberId: string) => void;
  setSelectedIndex: (index: number | null) => void;
  clearPrescription: (memberId: string) => void;
  hasCachedData: boolean;
  setCurrentMember: (memberId: string | null) => void;
}

// Criar Context
const PrescriptionContext = createContext<PrescriptionContextType | undefined>(undefined);

// Chave do SessionStorage
const STORAGE_KEY = 'magistral_prescription_analysis';

// Provider Component
interface PrescriptionProviderProps {
  children: ReactNode;
}

export const PrescriptionProvider: React.FC<PrescriptionProviderProps> = ({ children }) => {
  const [medications, setMedicationsState] = useState<AnalyzedMedication[]>([]);
  const [selectedIndex, setSelectedIndexState] = useState<number | null>(null);
  const [hasCachedData, setHasCachedData] = useState(false);
  const [currentMemberId, setCurrentMemberId] = useState<string | null>(null);

  // Carregar dados de um membro específico do SessionStorage
  const loadMemberData = (memberId: string) => {
    try {
      const cached = sessionStorage.getItem(STORAGE_KEY);
      if (cached) {
        const prescriptionMap: PrescriptionMap = JSON.parse(cached);
        const memberData = prescriptionMap[memberId];
        
        if (memberData) {
          // Verificar se os dados não estão expirados (1 hora)
          const ONE_HOUR = 60 * 60 * 1000;
          const isExpired = Date.now() - memberData.timestamp > ONE_HOUR;
          
          if (!isExpired && memberData.medications.length > 0) {
            setMedicationsState(memberData.medications);
            setSelectedIndexState(memberData.selectedIndex);
            setHasCachedData(true);
            console.log(`📋 [PrescriptionContext] Dados restaurados para membro ${memberId}:`, memberData.medications.length, 'medicamentos');
            return;
          } else if (isExpired) {
            // Limpar apenas os dados deste membro
            delete prescriptionMap[memberId];
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(prescriptionMap));
            console.log(`⏰ [PrescriptionContext] Cache do membro ${memberId} expirado, limpando...`);
          }
        }
      }
      
      // Limpar estado se não há dados válidos
      setMedicationsState([]);
      setSelectedIndexState(null);
      setHasCachedData(false);
    } catch (error) {
      console.error('❌ [PrescriptionContext] Erro ao carregar cache:', error);
      setMedicationsState([]);
      setSelectedIndexState(null);
      setHasCachedData(false);
    }
  };

  // Quando currentMemberId mudar, carregar dados daquele membro
  useEffect(() => {
    if (currentMemberId) {
      loadMemberData(currentMemberId);
    } else {
      setMedicationsState([]);
      setSelectedIndexState(null);
      setHasCachedData(false);
    }
  }, [currentMemberId]);

  // Salvar no SessionStorage quando mudar (por membro)
  const setMedications = (newMedications: AnalyzedMedication[], memberId: string) => {
    setMedicationsState(newMedications);
    
    try {
      // Carregar mapa existente
      const cached = sessionStorage.getItem(STORAGE_KEY);
      const prescriptionMap: PrescriptionMap = cached ? JSON.parse(cached) : {};
      
      if (newMedications.length > 0) {
        // Atualizar dados deste membro
        prescriptionMap[memberId] = {
          medications: newMedications,
          selectedIndex,
          timestamp: Date.now(),
          memberId
        };
        setHasCachedData(true);
        console.log(`💾 [PrescriptionContext] Dados salvos para membro ${memberId}:`, newMedications.length, 'medicamentos');
      } else {
        // Remover dados deste membro
        delete prescriptionMap[memberId];
        setHasCachedData(false);
        console.log(`🧹 [PrescriptionContext] Dados do membro ${memberId} removidos`);
      }
      
      // Salvar mapa atualizado
      if (Object.keys(prescriptionMap).length > 0) {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(prescriptionMap));
      } else {
        sessionStorage.removeItem(STORAGE_KEY);
      }
    } catch (error) {
      console.error('❌ [PrescriptionContext] Erro ao salvar cache:', error);
    }
  };

  const setSelectedIndex = (index: number | null) => {
    setSelectedIndexState(index);
    
    if (!currentMemberId) return;
    
    // Atualizar também no SessionStorage
    try {
      const cached = sessionStorage.getItem(STORAGE_KEY);
      if (cached) {
        const prescriptionMap: PrescriptionMap = JSON.parse(cached);
        if (prescriptionMap[currentMemberId]) {
          prescriptionMap[currentMemberId].selectedIndex = index;
          sessionStorage.setItem(STORAGE_KEY, JSON.stringify(prescriptionMap));
        }
      }
    } catch (error) {
      console.error('❌ [PrescriptionContext] Erro ao atualizar índice:', error);
    }
  };

  const clearPrescription = (memberId: string) => {
    try {
      const cached = sessionStorage.getItem(STORAGE_KEY);
      if (cached) {
        const prescriptionMap: PrescriptionMap = JSON.parse(cached);
        delete prescriptionMap[memberId];
        
        if (Object.keys(prescriptionMap).length > 0) {
          sessionStorage.setItem(STORAGE_KEY, JSON.stringify(prescriptionMap));
        } else {
          sessionStorage.removeItem(STORAGE_KEY);
        }
      }
      
      // Se for o membro atual, limpar estado
      if (memberId === currentMemberId) {
        setMedicationsState([]);
        setSelectedIndexState(null);
        setHasCachedData(false);
      }
      
      console.log(`🧹 [PrescriptionContext] Cache do membro ${memberId} limpo`);
    } catch (error) {
      console.error('❌ [PrescriptionContext] Erro ao limpar cache:', error);
    }
  };

  const setCurrentMember = (memberId: string | null) => {
    setCurrentMemberId(memberId);
  };

  const value: PrescriptionContextType = {
    medications,
    selectedIndex,
    setMedications,
    setSelectedIndex,
    clearPrescription,
    hasCachedData,
    setCurrentMember
  };

  return (
    <PrescriptionContext.Provider value={value}>
      {children}
    </PrescriptionContext.Provider>
  );
};

// Hook customizado para usar o Context
// eslint-disable-next-line react-refresh/only-export-components
export const usePrescriptionContext = (): PrescriptionContextType => {
  const context = useContext(PrescriptionContext);
  
  if (context === undefined) {
    throw new Error('usePrescriptionContext deve ser usado dentro de um PrescriptionProvider');
  }
  
  return context;
};

// Exportar também o Context para casos avançados
export default PrescriptionContext;
